import { useDeleteCompetitionTeamMutation } from '../../teams/mutations';
import { useDeleteCompetitionDivisionMutation } from '../mutations';
import { useCompetitionTeamListQuery } from '../../teams/queries';
import { ConfirmDialog } from '~/components/ConfirmDialog/ConfirmDialog';
import { ContextMenu } from '~/components/ContextMenu/ContextMenu';
import { CompetitionDivision, CompetitionTeam } from '~/apis/ssf';
import { scoringSystemNameToLabel } from '~/utils/scoring-system';
import { useCompetitionDivisionListQuery } from '../queries';
import { useAuthentication } from '~/hooks/useAuthentication';
import styles from './competition-divisions-teams.module.css';
import { Skeleton } from '~/components/Skeleton/Skeleton';
import { genderNameToLabel } from '~/utils/gender';
import { Table } from '~/components/Table/Table';
import { Link } from '~/components/Link/Link';
import { useDialog } from '~/hooks/useDialog';
import { Fragment } from 'react/jsx-runtime';
import { toast } from 'react-toastify';
import { useMemo } from 'react';

interface Props {
  competitionId: number;
}

interface MetaItemProps {
  label: string;
  value: string | number;
}

function MetaItem({ label, value }: MetaItemProps) {
  return (
    <div className={styles.metaItem}>
      <span className={styles.metaLabel}>{label}</span>
      <span className={styles.metaValue}>{value}</span>
    </div>
  );
}

interface TeamRowProps {
  team: CompetitionTeam;
  competitionId: number;
  canManageDivision: boolean;
}

function TeamRow({ team, competitionId, canManageDivision }: TeamRowProps) {
  const deleteMutation = useDeleteCompetitionTeamMutation();
  const dialog = useDialog();

  const { can } = useAuthentication();

  async function handleDelete() {
    await toast.promise(deleteMutation.mutateAsync(team.id), {
      success: 'Laget har tagits bort',
      pending: 'Tar bort laget...',
    });
  }

  const canEditAsClubAdmin = team.club ? can('edit-club', team.club.id) : false;
  const canEdit = canManageDivision || canEditAsClubAdmin;

  return (
    <Fragment>
      <Table.Row>
        <Table.Col>{team.name}</Table.Col>
        <Table.Col>{team.club ? <Link to={`/clubs/${team.club.id}`}>{team.club.name}</Link> : '-'}</Table.Col>
        {canManageDivision && (
          <Table.Col align="right">
            {canEdit && (
              <Table.Context
                items={[
                  <ContextMenu.Item
                    label="Redigera"
                    key="edit"
                    to={`/competitions/${competitionId}/teams/${team.id}/edit`}
                  />,
                  <ContextMenu.Item label="Ta bort" key="delete" onClick={dialog.open} danger />,
                ]}
              />
            )}
          </Table.Col>
        )}
      </Table.Row>

      <ConfirmDialog {...dialog} isLoading={deleteMutation.isPending} onConfirm={handleDelete} />
    </Fragment>
  );
}

interface DivisionCardProps {
  division: CompetitionDivision;
  teams: CompetitionTeam[];
  competitionId: number;
  canManageDivision: boolean;
}

function DivisionCard({ division, teams, competitionId, canManageDivision }: DivisionCardProps) {
  const deleteMutation = useDeleteCompetitionDivisionMutation();
  const dialog = useDialog();

  async function handleDelete() {
    await toast.promise(deleteMutation.mutateAsync(division.id), {
      success: 'Lagtävlingsdivisionen har tagits bort',
      pending: 'Tar bort lagtävlingsdivision...',
    });
  }

  const cols = canManageDivision ? 3 : 2;

  const nominationPeriod = [division.nomination_start_date, division.nomination_end_date].filter(Boolean).join(' – ');

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div>
          <div className={styles.title}>{division.name}</div>
          <div className={styles.meta}>
            <MetaItem label="Tävlingstyp" value={division.competition_type.description} />
            {division.age_categories.length > 0 && (
              <MetaItem label="Ålderskategorier" value={division.age_categories.map(a => a.label).join(', ')} />
            )}
            <MetaItem
              label="Kön"
              value={division.gender ? genderNameToLabel(division.gender, { mixedLabel: 'Mixad' }) : '-'}
            />
            <MetaItem label="Poängsystem" value={scoringSystemNameToLabel(division.scoring_system)} />
            <MetaItem label="Lyftare per lag" value={division.lifters_per_team} />
            <MetaItem label="Max antal reserver" value={division.reserve_slots ?? 0} />
            {division.gender === 'B' && (
              <MetaItem
                label="Minst antal damer/herrar"
                value={`${division.min_female_lifters ?? 0} / ${division.min_male_lifters ?? 0}`}
              />
            )}
            {nominationPeriod && <MetaItem label="Nominering" value={nominationPeriod} />}
          </div>
        </div>

        {canManageDivision && (
          <Table.Context
            items={[
              <ContextMenu.Item
                label="Redigera"
                key="edit"
                to={`/competitions/${competitionId}/divisions/${division.id}/edit`}
              />,
              <ContextMenu.Item label="Ta bort" key="delete" onClick={dialog.open} danger />,
            ]}
          />
        )}
      </div>

      <Table bg="even">
        <Table.Head>
          <Table.Row>
            <Table.Col as="th">Lag</Table.Col>
            <Table.Col as="th">Förening</Table.Col>
            {canManageDivision && <Table.Col as="th" className={styles.actionsCol} />}
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {teams.length === 0 ? (
            <Table.Row>
              <Table.Col colSpan={cols}>
                <div style={{ textAlign: 'center' }}>Inga lag har skapats ännu.</div>
              </Table.Col>
            </Table.Row>
          ) : (
            teams.map(team => (
              <TeamRow key={team.id} team={team} competitionId={competitionId} canManageDivision={canManageDivision} />
            ))
          )}
        </Table.Body>
      </Table>

      <ConfirmDialog {...dialog} isLoading={deleteMutation.isPending} onConfirm={handleDelete} />
    </div>
  );
}

export function CompetitionDivisionsTeams({ competitionId }: Props) {
  const { can } = useAuthentication();

  const { data: divisions, isLoading: isLoadingDivisions } = useCompetitionDivisionListQuery({
    competition: competitionId,
    page_size: 999,
  });
  const { data: teams, isLoading: isLoadingTeams } = useCompetitionTeamListQuery({
    competition: competitionId,
    page_size: 999,
  });

  const teamsByDivision = useMemo(() => {
    const map = new Map<number, CompetitionTeam[]>();
    for (const team of teams?.results ?? []) {
      const list = map.get(team.division.id) ?? [];
      list.push(team);
      map.set(team.division.id, list);
    }
    return map;
  }, [teams]);

  const canManageDivision = can('manage-competition-division', competitionId);

  if (isLoadingDivisions || isLoadingTeams) {
    return (
      <div className={styles.list}>
        {[0, 1].map(i => (
          <div className={styles.card} key={i}>
            <div className={styles.header}>
              <Skeleton style={{ width: '40%', height: 'var(--size-6)' }} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!divisions?.results.length) {
    return <div className={styles.empty}>Inga lagtävlingsdivisioner har skapats för denna tävling ännu.</div>;
  }

  return (
    <div className={styles.list}>
      {divisions.results.map(division => (
        <DivisionCard
          key={division.id}
          division={division}
          teams={teamsByDivision.get(division.id) ?? []}
          competitionId={competitionId}
          canManageDivision={canManageDivision}
        />
      ))}
    </div>
  );
}
